import React, { useState, useEffect } from 'react';
import { useNavigate } from "react-router-dom";
import '../../Stylesheets/Form.css';
import { v4 as uuidv4 } from "uuid";
import { apiFetch } from "../../utils/api";
import axios from 'axios';
import Snackbar from "../Snackbar";

// Helper function to add months to the current date
const addMonths = (months) => {
    const currentDate = new Date();
    const day = currentDate.getDate();
    currentDate.setDate(1);
    const targetMonth = currentDate.getMonth() + months;
    currentDate.setMonth(targetMonth);
    currentDate.setDate(Math.min(day, new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0).getDate()));
    return currentDate.toISOString().split('T')[0];
};

function REInput({ userId, contactPrefill }) {
    const navigate = useNavigate();
    const generateContactId = () => uuidv4();

    const mapContactToListingPrefill = (contact) => {
        if (!contact) return {};
        const rawName = typeof contact.name === "string" ? contact.name.trim() : "";
        const [derivedFirst = "", ...rest] = rawName ? rawName.split(/\s+/) : [];

        return {
            first_name: contact.first_name ?? contact.firstName ?? derivedFirst,
            last_name: contact.last_name ?? contact.lastName ?? rest.join(" "),
            phone: contact.phone ?? contact.phone_number ?? "",
            email: contact.email ?? "",
            mail_street: contact.mail_street ?? contact.mailStreet ?? "",
            mail_city: contact.mail_city ?? contact.mailCity ?? "",
            mail_state: contact.mail_state ?? contact.mailState ?? "",
            mail_zip: contact.mail_zip ?? contact.mailZip ?? "",
            contact_id: contact.contact_id ?? contact.contactId ?? contact.id ?? null,
        };
    };
    const contactPrefillData = mapContactToListingPrefill(contactPrefill);

    // Map property types to their subtypes (expanded list from ContactInput)
    const subTypesByPropertyType = {
        residential: [
            "Single-Family", "Condo", "Townhouse", "Duplex", "Triplex", "Quadraplex", "Multi-Family", "Apartment", "Co-op",
            "Mobile on Owned Land", "Manufactured Home", "Half Duplex/Villa", "Penthouse", "Loft", "Bungalow", "Cottage",
            "Patio Home", "Modular Home", "Student Housing", "Senior Living",
        ],
        commercial: [
            "Retail", "Office", "Industrial", "Mixed-Use", "Medical Office", "Hospitality", "Restaurant", "Shopping Center",
            "Warehouse", "Self-Storage", "Data Center", "Flex Space", "Gas Station", "Car Wash", "Daycare Center", "Automotive",
            "Recreational", "Religious Facility",
        ],
        land: [
            "Vacant", "Agricultural", "Ranch", "Development", "Timberland", "Recreational", "Waterfront", "Commercial Lot",
            "Residential Lot", "Industrial Lot", "Orchard/Vineyard", "Conservation", "Equestrian", "Mining", "Wetland",
        ],
        other: [
            "Mobile - Rented Land", "Tiny Home", "Special Use", "Uncategorized", "Boat Slip", "Timeshare", "Parking Lot",
            "RV Park", "Cemetery", "Historical Property", "Live/Work Unit", "Floating Home", "Air Rights", "Seasonal Cabin",
            "Off-Grid Property", "Mixed-Use Other",
        ],
    };

    const [contactsCache, setContactsCache] = useState([]);
    const [contactsLoaded, setContactsLoaded] = useState(false);
    const [matchedContact, setMatchedContact] = useState(null);
    const [listing, setListing] = useState(() => {
        const contactId = contactPrefillData.contact_id || generateContactId();

        return {
            first_name: '',
            last_name: '',
            phone: '',
            email: '',
            mail_street: '',
            mail_city: '',
            mail_state: '',
            mail_zip: '',
            street_address: '',
            city: '',
            state: '',
            zip_code: '',
            property_type: '',
            property_subtype: '',
            zoning: '',
            status: '',
            bedrooms: '',
            bathrooms: '',
            lot_acres: '',
            list_price: '',
            buyer_fee: '',
            parcel_number: '',
            mls_id: '',
            listing_link: '',
            image_url: '',
            property_description: '',
            title_company: '',
            // Default expiration: 1 year from today
            listing_expires: addMonths(12),
            contract_length: '',
            user_id: userId,
            contact_id: contactId,
            role: "seller",
            ...contactPrefillData,
            contact_id: contactId,
        };
    });
    console.log("Initial listing state:", listing);

    const [isSearching, setIsSearching] = useState(false);
    const [searchError, setSearchError] = useState("");
    // When true, keep listing address in sync with seller mailing address
    const [listingSameAsMail, setListingSameAsMail] = useState(false);
    // Internal DB address search
    const [addrMatches, setAddrMatches] = useState([]);
    const [isAddrSearching, setIsAddrSearching] = useState(false);
    const [snackbar, setSnackbar] = useState({ show: false, message: '', type: 'success' });
    const [isLoadingContacts, setIsLoadingContacts] = useState(false);

    useEffect(() => {
        if (!contactPrefill) return;
        const mapped = mapContactToListingPrefill(contactPrefill);
        if (Object.keys(mapped).length === 0) return;

        setListing((prev) => ({
            ...prev,
            ...mapped,
            contact_id: mapped.contact_id || prev.contact_id || generateContactId(),
        }));
    }, [contactPrefill]);

    const normalize = (str) => (str || '').trim().toLowerCase();

    const findBestContact = (first, last) => {
        if (!Array.isArray(contactsCache) || contactsCache.length === 0) return null;
        const firstNorm = normalize(first);
        const lastNorm = normalize(last);
        if (!firstNorm) return null;

        // Prefer full first+last match, fallback to first-only
        const exact = contactsCache.find((c) => normalize(c.first_name || c.name) === firstNorm && normalize(c.last_name) === lastNorm && lastNorm);
        if (exact) return exact;
        return contactsCache.find((c) => normalize(c.first_name || c.name) === firstNorm) || null;
    };

    useEffect(() => {
        const shouldFetch = normalize(listing.first_name).length >= 2 && !contactsLoaded && !isLoadingContacts;
        if (!shouldFetch || !userId) return;

        const fetchContacts = async () => {
            setIsLoadingContacts(true);
            try {
                const res = await apiFetch("fetchContacts", { queryParams: { user_id: userId } });
                if (res.success) {
                    setContactsCache(res.contacts || []);
                    setContactsLoaded(true);
                }
            } catch (err) {
                console.error("Contact lookup failed:", err);
            } finally {
                setIsLoadingContacts(false);
            }
        };

        fetchContacts();
    }, [listing.first_name, contactsLoaded, isLoadingContacts, userId]);

    useEffect(() => {
        const match = findBestContact(listing.first_name, listing.last_name);
        setMatchedContact(match);
        if (!match) return;

        const derivedName = typeof match.name === "string" ? match.name.split(" ") : [];
        const fallbackLast = derivedName.length > 1 ? derivedName.slice(1).join(" ") : "";

        setListing((prev) => ({
            ...prev,
            contact_id: match.id || prev.contact_id,
            last_name: prev.last_name || match.last_name || fallbackLast || prev.last_name,
            phone: prev.phone || match.phone || "",
            email: prev.email || match.email || "",
            mail_street: prev.mail_street || match.mail_street || match.mailAddress || "",
            mail_city: prev.mail_city || match.mail_city || match.mailCity || "",
            mail_state: prev.mail_state || match.mail_state || match.mailSt || "",
            mail_zip: prev.mail_zip || match.mail_zip || match.mailZip || "",
        }));
    }, [listing.first_name, listing.last_name, contactsCache]);

    // Helper function to capitalize the first letter of a string
    const capitalizeFirstLetter = (str) => {
        if (!str) return str;
        return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
    };

    // Helper function to format phone number as (XXX) XXX-XXXX
    const formatPhoneNumber = (value) => {
        const digits = value.replace(/\D/g, "");
        const trimmedDigits = digits.slice(0, 10);

        if (trimmedDigits.length <= 3) {
            return trimmedDigits;
        } else if (trimmedDigits.length <= 6) {
            return `(${trimmedDigits.slice(0, 3)}) ${trimmedDigits.slice(3)}`;
        } else {
            return `(${trimmedDigits.slice(0, 3)}) ${trimmedDigits.slice(3, 6)}-${trimmedDigits.slice(6)}`;
        }
    };

    // Handle input changes with formatting
    const handleChange = (e) => {
        const { name, value } = e.target;

        // Fields that should NOT be capitalized
        const nonCapitalizedFields = [
            "email", "phone","mail_street", "mail_zip", "zip_code", "parcel_number", "mls_id",
            "listing_link", "property_description", "buyer_fee",
            // Do not change case for select-backed fields
            "property_type", "property_subtype", "zoning", "status",
        ];

        // Fields that are phone numbers
        const phoneFields = ["phone"];

        let formattedValue = value;

        if (phoneFields.includes(name)) {
            formattedValue = formatPhoneNumber(value);
        } else if (!nonCapitalizedFields.includes(name)) {
            formattedValue = capitalizeFirstLetter(value);
        }

        setListing((prev) => ({
            ...prev,
            [name]: formattedValue,
            ...(name === 'property_type' ? { property_subtype: '' } : {}),
        }));
    };

    // Debounced DB search when street address changes
    useEffect(() => {
        const street = (listing.street_address || '').trim();
        if (street.length < 3) { setAddrMatches([]); return; }
        let cancelled = false;
        const t = setTimeout(async () => {
            try {
                setIsAddrSearching(true);
                const data = await apiFetch('searchProperties', {
                    queryParams: {
                        q: street,
                        city: listing.city || '',
                        state: listing.state || '',
                        zip: listing.zip_code || ''
                    }
                });
                if (!cancelled) {
                    setAddrMatches(Array.isArray(data.properties) ? data.properties : []);
                }
            } catch (e) {
                if (!cancelled) setAddrMatches([]);
            } finally {
                if (!cancelled) setIsAddrSearching(false);
            }
        }, 300);
        return () => { cancelled = true; clearTimeout(t); };
    }, [listing.street_address, listing.city, listing.state, listing.zip_code]);

    const applyMatch = (prop) => {
        if (!prop) return;
        setListing((prev) => ({
            ...prev,
            street_address: prop.street_address || prev.street_address,
            city: prop.city || prev.city,
            state: prop.state || prev.state,
            zip_code: prop.zip_code || prev.zip_code,
            parcel_number: prop.parcel_number || prev.parcel_number,
            bedrooms: prop.bedrooms ?? prev.bedrooms,
            bathrooms: prop.bathrooms ?? prev.bathrooms,
            lot_acres: prop.acreage ?? prev.lot_acres,
            property_type: prop.property_type || prev.property_type,
            property_subtype: prop.property_subtype || prev.property_subtype,
            zoning: prop.zoning || prev.zoning,
            property_description: prop.property_description || prev.property_description,
        }));
        setAddrMatches([]);
        setSnackbar({ show: true, message: 'Matched existing property from DB.', type: 'success' });
    };

    const handleListingExpiresChange = (months) => {
        setListing((prev) => ({
            ...prev,
            listing_expires: addMonths(parseInt(months, 10)),
            contract_length: months.toString(),
        }));
    };

    const handleCustomDateChange = (e) => {
        setListing((prev) => ({
            ...prev,
            listing_expires: e.target.value,
            contract_length: '',
        }));
    };

    // Keep listing address in sync with mailing address when checkbox is enabled
    useEffect(() => {
        if (listingSameAsMail) {
            setListing((prev) => ({
                ...prev,
                street_address: prev.mail_street,
                city: prev.mail_city,
                state: prev.mail_state,
                zip_code: prev.mail_zip,
            }));
        }
    }, [listingSameAsMail, listing.mail_street, listing.mail_city, listing.mail_state, listing.mail_zip]);

    // Build a single-line address from listing fields
    const buildAddress = () => {
        const parts = [
            listing.street_address,
            listing.city,
            listing.state,
            listing.zip_code,
        ].filter(Boolean);
        return parts.join(", ");
    };

    const toNumber = (val) => {
        if (val === null || val === undefined) return undefined;
        if (typeof val === 'number') return val;
        const cleaned = String(val).replace(/[^0-9.]/g, '');
        if (!cleaned) return undefined;
        const n = Number(cleaned);
        return Number.isFinite(n) ? n : undefined;
    };

    const extractAcres = (sr, reso) => {
        // Prefer explicit lot area if provided
        if (sr?.lotAreaValue) {
            const val = Number(sr.lotAreaValue);
            const units = (sr.lotAreaUnits || '').toLowerCase();
            if (Number.isFinite(val)) {
                if (units.includes('acre')) return val;
                if (units.includes('square') || units.includes('sq')) return +(val / 43560).toFixed(2);
            }
        }
        // Try resoFacts.lotSize e.g., "10,008 sqft" or "0.25 acres"
        if (reso?.lotSize) {
            const sizeStr = String(reso.lotSize).toLowerCase();
            const num = toNumber(sizeStr);
            if (Number.isFinite(num)) {
                if (sizeStr.includes('acre')) return +num.toFixed(2);
                if (sizeStr.includes('sq')) return +(num / 43560).toFixed(2);
            }
        }
        return undefined;
    };

    const mapHomeTypeToCategory = (homeType) => {
        const ht = (homeType || '').toLowerCase();
        if (!ht) return undefined;
        if (['single_family','singlefamily','single-family','condo','townhouse','duplex','triplex','quadraplex','apartment','co-op','manufactured','mobile','bungalow','loft','penthouse'].some(k => ht.includes(k))) {
            return 'residential';
        }
        if (['lot','land'].some(k => ht.includes(k))) return 'land';
        if (['office','retail','industrial','warehouse','commercial'].some(k => ht.includes(k))) return 'commercial';
        return 'other';
    };

    const normalizeSubtype = (ptype, raw) => {
        const r = (raw || '').toLowerCase();
        if (!ptype) return raw || '';
        if (ptype === 'land') {
            if (/(lot|vacant|unimproved|land)/.test(r)) return 'Vacant';
            if (/agri|ranch|farm/.test(r)) return 'Agricultural';
            if (/dev|subdiv/.test(r)) return 'Development';
            if (/water/.test(r)) return 'Waterfront';
            return 'Vacant';
        }
        if (ptype === 'residential') {
            if (/single/.test(r)) return 'Single-Family';
            if (/condo/.test(r)) return 'Condo';
            if (/town/.test(r)) return 'Townhouse';
            if (/duplex/.test(r)) return 'Duplex';
            if (/triplex/.test(r)) return 'Triplex';
            if (/quad/.test(r)) return 'Quadraplex';
            if (/apartment|multi/.test(r)) return 'Multi-Family';
            if (/manufactured|mobile/.test(r)) return 'Manufactured Home';
        }
        if (ptype === 'commercial') {
            if (/retail/.test(r)) return 'Retail';
            if (/office/.test(r)) return 'Office';
            if (/industrial|warehouse/.test(r)) return 'Industrial';
            if (/mixed/.test(r)) return 'Mixed-Use';
            if (/medical/.test(r)) return 'Medical Office';
        }
        return raw || '';
    };

    const mapStatus = (raw) => {
        const s = (raw || '').toLowerCase();
        if (!s) return undefined;
        if (s.includes('for_sale') || s.includes('for sale') || s.includes('active')) return 'Active';
        if (s.includes('pending') || s.includes('contingent')) return 'Pending';
        if (s.includes('sold') || s.includes('closed')) return 'Sold';
        if (s.includes('expired')) return 'Expired';
        if (s.includes('withdrawn')) return 'Withdrawn';
        if (s.includes('canceled') || s.includes('cancelled')) return 'Canceled';
        return undefined;
    };

    // Populate listing fields from a Zillow payload
    const populateFromPayload = (payload) => {
        const sr = payload?.searchResults || payload;
        console.log("Zillow payload:", payload);    
        const reso = sr?.resoFacts || {};
        const addr = sr?.address || {};

        const bedrooms = sr?.bedrooms ?? reso?.bedrooms ?? undefined;
        const bathrooms = sr?.bathrooms ?? reso?.bathroomsFloat ?? reso?.bathrooms ?? undefined;
        const price = sr?.price ?? payload?.price ?? payload?.zestimate ?? undefined;
        const parcel = reso?.parcelNumber || sr?.parcelId || payload?.parcelId || undefined;
        const homeTypeRaw = sr?.homeType || reso?.homeType || sr?.propertyTypeDimension || undefined;
        const property_type = mapHomeTypeToCategory(homeTypeRaw);
        const subtypeRaw = (Array.isArray(reso?.propertySubType) && reso.propertySubType[0])
            || reso?.structureType
            || sr?.propertyTypeDimension
            || homeTypeRaw
            || undefined;
        const property_subtype = normalizeSubtype(property_type, subtypeRaw);
        const lot_acres = extractAcres(sr, reso);
        const status = mapStatus(sr?.homeStatus || payload?.homeStatus);
        const mls_id = sr?.mlsId || payload?.mlsid || payload?.attributionInfo?.mlsId || undefined;
        const listing_link = sr?.hdpUrl || sr?.url || payload?.url ? `https://www.zillow.com${sr?.hdpUrl || sr?.url || payload?.url}` : undefined;
        const image_url = sr?.imgSrc || (Array.isArray(payload?.neighborhoodMapThumb) && payload.neighborhoodMapThumb[0]?.url) || sr?.desktopWebHdpImageLink || payload?.hiResImageLink || undefined;
        const zoning = reso?.zoningDescription || reso?.zoning || undefined;

        setListing((prev) => ({
            ...prev,
            street_address: addr?.streetAddress || sr?.streetAddress || prev.street_address,
            city: addr?.city || sr?.city || prev.city,
            state: addr?.state || sr?.state || prev.state,
            zip_code: addr?.zipcode || sr?.zipcode || prev.zip_code,
            bedrooms: bedrooms ?? prev.bedrooms,
            bathrooms: bathrooms ?? prev.bathrooms,
            list_price: price ?? prev.list_price,
            parcel_number: parcel ?? prev.parcel_number,
            lot_acres: lot_acres ?? prev.lot_acres,
            property_type: property_type ?? prev.property_type,
            property_subtype: property_subtype || prev.property_subtype,
            zoning: zoning ?? prev.zoning,
            status: status ?? prev.status,
            property_description: sr?.description || payload?.description || prev.property_description,
            image_url: image_url ?? prev.image_url,
            mls_id: mls_id ?? prev.mls_id,
            listing_link: listing_link ?? prev.listing_link,
        }));
    };

    // Removed candidate list helpers; single result flow

    const handleSearchAddress = async () => {
        setSearchError("");
        const location = buildAddress();
        if (!location) {
            setSearchError('Please enter at least street, city, and state.');
            return;
        }
        setIsSearching(true);
        try {
            const key = process.env.REACT_APP_RAPIDAPI_KEY;
            if (!key) {
                throw new Error('Missing RapidAPI key. Add REACT_APP_RAPIDAPI_KEY to your .env');
            }
            const options = {
                method: 'GET',
                url: 'https://private-zillow.p.rapidapi.com/pro/byaddress',
                params: { address: location },
                headers: {
                    'x-rapidapi-key': key,
                    'x-rapidapi-host': 'private-zillow.p.rapidapi.com'
                }
            };

            const response = await axios.request(options);
            const data = response?.data || {};
            populateFromPayload(data);
            setSnackbar({ show: true, message: 'Property details applied.', type: 'success' });
        } catch (err) {
            console.error('Address search error:', err);
            setSearchError(err?.message || 'Failed to fetch property details.');
        } finally {
            setIsSearching(false);
        }
    };


    // Removed candidate selection; search populates immediately

    const handleSubmit = async (e) => {
        e.preventDefault();
        // Clean payload: convert empty strings to null
        const payload = Object.fromEntries(
            Object.entries(listing).map(([key, value]) => [
                key,
                value === '' ? null : value
            ])
        );

        try {
            // Use dedicated createListing endpoint which returns parsed JSON
            const result = await apiFetch("createListing", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(payload),
            });
            if (result.success) {
                // If we matched an existing contact, backfill any missing fields
                if (matchedContact?.id) {
                    const patch = {};
                    const fields = [
                        ["first_name", "first_name"],
                        ["last_name", "last_name"],
                        ["phone", "phone"],
                        ["email", "email"],
                        ["mail_street", "mail_street"],
                        ["mail_city", "mail_city"],
                        ["mail_state", "mail_state"],
                        ["mail_zip", "mail_zip"],
                    ];

                    fields.forEach(([field, key]) => {
                        const contactVal = matchedContact[field] ?? matchedContact[key] ?? null;
                        const listingVal = listing[key] ?? listing[field] ?? null;
                        if ((!contactVal || contactVal === "") && listingVal) {
                            patch[key] = listingVal;
                        }
                    });

                    if (Object.keys(patch).length > 0) {
                        try {
                            await apiFetch("fillContactGaps", {
                                method: "POST",
                                body: JSON.stringify({
                                    contact_id: matchedContact.id,
                                    ...patch,
                                }),
                            });
                        } catch (err) {
                            console.error("fillContactGaps failed:", err);
                        }
                    }
                }

                setSnackbar({ show: true, message: "✅ Listing created successfully!", type: 'success' });
                setTimeout(() => navigate("/"), 1000);
                setListing({
                    first_name: '',
                    last_name: '',
                    phone: '',
                    email: '',
                    mail_street: '',
                    mail_city: '',
                    mail_state: '',
                    mail_zip: '',
                    street_address: '',
                    city: '',
                    state: '',
                    zip_code: '',
                    property_type: '',
                    property_subtype: '',
                    zoning: '',
                    status: '',
                    bedrooms: '',
                    bathrooms: '',
                    lot_acres: '',
                list_price: '',
                buyer_fee: '',
                parcel_number: '',
                    mls_id: '',
                    listing_link: '',
                    image_url: '',
                    property_description: '',
                    title_company: '',
                    listing_expires: '',
                    contract_length: '',
                    user_id: userId,
                    contact_id: generateContactId(),
                });
            } else {
                setSnackbar({ show: true, message: `❌ ${result.message}`, type: 'danger' });
            }
        } catch (error) {
            console.error("Submission Error:", error);
            setSnackbar({ show: true, message: `❌ ${error.message || 'Submission failed.'}`, type: 'danger' });
        }
    };

    return (
        <>
        <Snackbar
          show={snackbar.show}
          message={snackbar.message}
          type={snackbar.type}
          onClose={() => setSnackbar((s) => ({ ...s, show: false }))}
        />
        <form onSubmit={handleSubmit} className="container mt-2">
            {/* Seller Information */}
            <h5>Seller Information</h5>
            <div className="row mb-2">
                {[
                    { name: 'first_name', label: 'First Name' },
                    { name: 'last_name', label: 'Last Name' },
                    { name: 'phone', label: 'Phone', type: 'tel' },
                    { name: 'email', label: 'Email', type: 'email' },
                ].map(({ name, label, type = 'text' }, idx) => (
                    <div className="col-md-3 mb-2" key={idx}>
                        <label htmlFor={name} className="form-label">{label}</label>
                        <input
                            type={type}
                            className="form-control"
                            id={name}
                            name={name}
                            value={listing[name]}
                            onChange={handleChange}
                            required={name !== 'email'} // Email is optional
                        />
                    </div>
                ))}
            </div>

            {/* Seller Mailing Address */}
            <h5>Seller Mailing Address</h5>
            <div className="row mb-2">
                {[
                    { name: 'mail_street', label: 'Mailing Street' },
                    { name: 'mail_city', label: 'Mailing City' },
                    { name: 'mail_state', label: 'Mailing State' },
                    { name: 'mail_zip', label: 'Mailing Zip' },
                ].map(({ name, label }, idx) => (
                    <div className="col-md-3 mb-2" key={idx}>
                        <label htmlFor={name} className="form-label">{label}</label>
                        <input
                            type="text"
                            className="form-control"
                            id={name}
                            name={name}
                            value={listing[name]}
                            onChange={handleChange}
                        />
                    </div>
                ))}
            </div>

            {/* Listing Address */}
            <div className="d-flex align-items-center justify-content-between">
                <h5 className="mb-0">Listing Address</h5>
                <div className="d-flex align-items-center gap-3">
                    <div className="form-check">
                        <input
                            className="form-check-input"
                            type="checkbox"
                            id="listingSameAsMail"
                            checked={listingSameAsMail}
                            onChange={(e) => setListingSameAsMail(e.target.checked)}
                        />
                        <label className="form-check-label ms-1" htmlFor="listingSameAsMail">
                            Same as seller mailing address
                        </label>
                    </div>
                    {searchError && (
                        <small className="text-danger me-2">{searchError}</small>
                    )}
                    {/* <button
                        type="button"
                        className="btn btn-outline-secondary btn-sm"
                        onClick={handleSearchAddress}
                        disabled={isSearching}
                    >
                        {isSearching ? 'Searching…' : 'Search Address'}
                    </button> */}
                </div>
            </div>
            <div className="row mb-2">
                {[
                    { name: 'street_address', label: 'Listing Street' },
                    { name: 'city', label: 'Listing City' },
                    { name: 'state', label: 'Listing State' },
                    { name: 'zip_code', label: 'Listing Zip' },
                ].map(({ name, label }, idx) => (
                    <div className="col-md-3 mb-2" key={idx}>
                        <label htmlFor={name} className="form-label">{label}</label>
                        <input
                            type="text"
                            className="form-control"
                            id={name}
                            name={name}
                            value={listing[name]}
                            onChange={handleChange}
                            required
                            disabled={listingSameAsMail}
                        />
                        {name === 'street_address' && addrMatches.length > 0 && (
                            <select
                                className="form-select form-select-sm mt-1"
                                onChange={(e) => {
                                    const idx = Number(e.target.value);
                                    const prop = addrMatches[idx];
                                    applyMatch(prop);
                                }}
                                defaultValue=""
                            >
                                <option value="" disabled>
                                    {isAddrSearching ? 'Searching…' : 'Select a matching address'}
                                </option>
                                {addrMatches.map((p, i) => (
                                    <option key={`${p.id}-${i}`} value={i}>
                                        {[p.street_address, p.city, p.state, p.zip_code].filter(Boolean).join(', ')}
                                    </option>
                                ))}
                            </select>
                        )}
                    </div>
                ))}
            </div>

            {/* Candidate selection removed; single result populated automatically */}

            {/* Property Type/Subtype/Zoning/Status */}
            <h5>Property Details</h5>
            <div className="row mb-2">
                <div className="col-md-3 mb-2">
                    <label htmlFor="property_type" className="form-label">Property Type</label>
                    <select
                        className="form-select"
                        id="property_type"
                        name="property_type"
                        value={listing.property_type}
                        onChange={handleChange}
                        required
                    >
                        <option value="">Select Type</option>
                        <option value="residential">Residential</option>
                        <option value="commercial">Commercial</option>
                        <option value="land">Land</option>
                        <option value="other">Other</option>
                    </select>
                </div>
                <div className="col-md-3 mb-2">
                    <label htmlFor="property_subtype" className="form-label">Property Subtype</label>
                    <select
                        className="form-select"
                        id="property_subtype"
                        name="property_subtype"
                        value={listing.property_subtype}
                        onChange={handleChange}
                        required
                    >
                        <option value="">Select Subtype</option>
                        {listing.property_type && subTypesByPropertyType[listing.property_type] ? (
                            subTypesByPropertyType[listing.property_type].map((subtype) => (
                                <option key={subtype} value={subtype}>
                                    {subtype}
                                </option>
                            ))
                        ) : null}
                    </select>
                </div>
                <div className="col-md-3 mb-2">
                    <label htmlFor="zoning" className="form-label">Zoning</label>
                    <select
                        className="form-select"
                        id="zoning"
                        name="zoning"
                        value={listing.zoning || ''}
                        onChange={handleChange}
                        required
                    >
                        <option value="">Select Zoning</option>
                        <optgroup label="Residential">
                            <option value="R1">R1 - Single-Family</option>
                            <option value="R2">R2 - Multi-Family</option>
                            <option value="R3">R3 - High-Density Res</option>
                        </optgroup>
                        <optgroup label="Commercial">
                            <option value="C1">C1 - Neighborhood</option>
                            <option value="C2">C2 - General</option>
                            <option value="C3">C3 - Heavy</option>
                        </optgroup>
                        <optgroup label="Industrial">
                            <option value="I1">I1 - Light</option>
                            <option value="I2">I2 - General</option>
                            <option value="I3">I3 - Heavy</option>
                        </optgroup>
                        <optgroup label="Agricultural">
                            <option value="A1">A1 - Agricultural</option>
                            <option value="A2">A2 - Rural Ag</option>
                        </optgroup>
                        <optgroup label="Mixed Use">
                            <option value="MU">MU - Mixed Use</option>
                            <option value="PUD">PUD - Planned Dev</option>
                        </optgroup>
                    </select>
                </div>
                <div className="col-md-3 mb-2">
                    <label htmlFor="status" className="form-label">Status</label>
                    <select
                        className="form-select"
                        id="status"
                        name="status"
                        value={listing.status || ''}
                        onChange={handleChange}
                        required
                    >
                        <option value="">Select Status</option>
                        <option value="Active">Active</option>
                        <option value="Pending">Pending</option>
                        <option value="Sold">Sold</option>
                        <option value="Expired">Expired</option>
                        <option value="Withdrawn">Withdrawn</option>
                        <option value="Canceled">Canceled</option>
                    </select>
                </div>
            </div>

            {/* Pricing & MLS */}
            <h5>Pricing & MLS</h5>
            <div className="row mb-2">
                <div className="col-md-3 mb-2">
                    <label htmlFor="list_price" className="form-label">List Price</label>
                    <input
                        type="number"
                        className="form-control"
                        id="list_price"
                        name="list_price"
                        value={listing.list_price}
                        onChange={handleChange}
                        required
                    />
                </div>
                <div className="col-md-3 mb-2">
                    <label htmlFor="buyer_fee" className="form-label">Buyers Compensation</label>
                    <input
                        type="number"
                        step="0.01"
                        className="form-control"
                        id="buyer_fee"
                        name="buyer_fee"
                        value={listing.buyer_fee || ''}
                        onChange={handleChange}
                    />
                </div>
                <div className="col-md-3 mb-2">
                    <label htmlFor="parcel_number" className="form-label">Parcel #</label>
                    <input
                        type="text"
                        className="form-control"
                        id="parcel_number"
                        name="parcel_number"
                        value={listing.parcel_number}
                        onChange={handleChange}
                    />
                </div>
                <div className="col-md-3 mb-2">
                    <label htmlFor="mls_id" className="form-label">MLS #</label>
                    <input
                        type="text"
                        className="form-control"
                        id="mls_id"
                        name="mls_id"
                        value={listing.mls_id}
                        onChange={handleChange}
                    />
                </div>
                <div className="col-md-3 mb-2">
                    <label htmlFor="listing_link" className="form-label">Listing Link</label>
                    <input
                        type="text"
                        className="form-control"
                        id="listing_link"
                        name="listing_link"
                        value={listing.listing_link}
                        onChange={handleChange}
                    />
                </div>
            </div>

            {/* Property Details */}
            <h5>Property Details</h5>
            <div className="row mb-2">
                <div className="col-md-3 mb-2">
                    <label htmlFor="bedrooms" className="form-label">Bedrooms</label>
                    <input
                        type="number"
                        className="form-control"
                        id="bedrooms"
                        name="bedrooms"
                        value={listing.bedrooms}
                        onChange={handleChange}
                    />
                </div>
                <div className="col-md-3 mb-2">
                    <label htmlFor="bathrooms" className="form-label">Bathrooms</label>
                    <input
                        type="number"
                        className="form-control"
                        id="bathrooms"
                        name="bathrooms"
                        value={listing.bathrooms}
                        onChange={handleChange}
                    />
                </div>
                <div className="col-md-3 mb-2">
                    <label htmlFor="lot_acres" className="form-label">Acres</label>
                    <input
                        type="number"
                        step="0.01"
                        className="form-control"
                        id="lot_acres"
                        name="lot_acres"
                        value={listing.lot_acres}
                        onChange={handleChange}
                    />
                </div>
                <div className="col-md-3 mb-2">
                    <label htmlFor="title_company" className="form-label">Desired Title Company</label>
                    <input
                        type="text"
                        className="form-control"
                        id="title_company"
                        name="title_company"
                        value={listing.title_company}
                        onChange={handleChange}
                    />
                </div>
            </div>

            {/* Contract & Expiration */}
            <h5>Contract & Expiration</h5>
            <div className="row mb-2">
                <div className="col-md-3 mb-2">
                    <label className="form-label">Quick Select</label>
                    <div className="d-flex gap-3">
                        {['3', '6', '9'].map((months) => (
                            <div className="form-check" key={months}>
                                <input
                                    type="radio"
                                    className="form-check-input"
                                    id={`expiresIn${months}Months`}
                                    name="contract_length"
                                    value={months}
                                    checked={listing.contract_length === months}
                                    onChange={() => handleListingExpiresChange(months)}
                                />
                                <label
                                    htmlFor={`expiresIn${months}Months`}
                                    className="form-check-label ms-1"
                                >
                                    {months} Mo
                                </label>
                            </div>
                        ))}
                    </div>
                </div>
                <div className="col-md-3 mb-2">
                    <label htmlFor="listing_expires" className="form-label">Expiration Date</label>
                    <input
                        type="date"
                        className="form-control"
                        id="listing_expires"
                        name="listing_expires"
                        value={listing.listing_expires}
                        onChange={handleCustomDateChange}
                    />
                </div>
                <div className="col-md-3 mb-2">
                    <label htmlFor="image_url" className="form-label">Image Link</label>
                    <input
                        type="text"
                        className="form-control"
                        id="image_url"
                        name="image_url"
                        placeholder="https://..."
                        value={listing.image_url}
                        onChange={handleChange}
                    />
                </div>
            </div>

            {/* Property Description */}
            <h5>Property Description</h5>
            <div className="row mb-2">
                <div className="col-md-12">
                    <label htmlFor="property_description" className="form-label">Property Description</label>
                    <textarea
                        className="form-control"
                        id="property_description"
                        name="property_description"
                        rows="2"
                        value={listing.property_description}
                        onChange={handleChange}
                    />
                </div>
            </div>

            {/* Submit Button */}
            <div className="row mb-2 text-center">
                <div className="col-md-12">
                    <button type="submit" className="btn btn-primary w-50">
                        Submit
                    </button>
                </div>
            </div>
        </form>
        </>
    );
}

export default REInput;
